import { titleCase } from "../../../utils";
import { getCountryFromCode } from "../../../utils/getCountryFromCode";
import {
  TConfigItemType,
  TVerificationAppFieldConfigItem,
} from "../../app/models";
import { SelectData } from "mui-rff";

export function resolveConfigFieldLabel(
  field: TVerificationAppFieldConfigItem
): string {
  if (typeof field.label === "string") return field.label;
  return titleCase(field.name ?? "");
}

export function isBooleanConfigField(
  field: TVerificationAppFieldConfigItem
): boolean {
  return (
    field.type === TConfigItemType.CHECKBOX || field.type === TConfigItemType.PEP
  );
}

export function formatBooleanConfigValue(value: unknown): string {
  if (value == null || value === "") return "";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") {
    if (value === 1) return "Yes";
    if (value === 0) return "No";
  }
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized === "true" || normalized === "yes") return "Yes";
    if (normalized === "false" || normalized === "no") return "No";
  }
  return String(value);
}

export function formatCountryDisplayValue(value: unknown): string {
  if (value == null || value === "") return "";
  const rawValue = String(value);
  return getCountryFromCode(rawValue) ?? rawValue;
}

export function formatUrlDisplayValue(value: unknown): string {
  if (value == null || value === "") return "";
  return String(value).trim();
}

export function isLikelyAbsoluteUrl(value: unknown): boolean {
  const displayValue = formatUrlDisplayValue(value);
  if (!displayValue) return false;

  try {
    const parsed = new URL(displayValue);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

export function validateUrlDisplayValue(value: unknown): string | undefined {
  if (value === undefined || value === null) return undefined;
  if (typeof value !== "string") return "Please enter a valid URL";

  const displayValue = formatUrlDisplayValue(value);
  if (!displayValue) return undefined;

  return isLikelyAbsoluteUrl(displayValue)
    ? undefined
    : "Please enter a valid URL";
}

export function normalizeSelectData(
  data?: string[] | SelectData[]
): SelectData[] {
  return (
    data?.map((dataItem) => {
      if (
        typeof dataItem !== "string" &&
        dataItem.value &&
        dataItem.label
      ) {
        return dataItem;
      }
      if (typeof dataItem === "string") {
        return { label: dataItem, value: dataItem };
      }
      return { label: "error", value: "error" };
    }) ?? []
  );
}

export function formatSelectDisplayValue(
  field: TVerificationAppFieldConfigItem,
  value: unknown
): string {
  if (value == null || value === "") return "";

  const options = normalizeSelectData(field.data);
  const labelByValue = new Map(
    options.map((option) => [String(option.value), String(option.label)])
  );
  const formatValue = (item: unknown): string => {
    const storedValue = String(item);
    return labelByValue.get(storedValue) ?? storedValue;
  };

  if (Array.isArray(value)) {
    return value.map(formatValue).filter(Boolean).join(", ");
  }

  return formatValue(value);
}

export function formatSourceOfActiveDisplayLines(value: unknown): string[] {
  if (value == null || value === "") return [];
  if (typeof value !== "string") return [String(value)];

  return value
    .split(", ")
    .flatMap((item) => {
      const trimmed = item.trim();
      if (!trimmed) return [];
      const otherPrefix = "Other:";
      if (!trimmed.startsWith(otherPrefix)) return [trimmed];

      const explanation = trimmed.slice(otherPrefix.length).trim();
      return explanation ? ["Other", explanation] : ["Other"];
    });
}

export function formatConfigFieldDisplayValue(
  field: TVerificationAppFieldConfigItem,
  value: unknown
): unknown {
  if (isBooleanConfigField(field)) {
    return formatBooleanConfigValue(value);
  }
  if (field.type === TConfigItemType.COUNTRY_SELECT) {
    return formatCountryDisplayValue(value);
  }
  if (field.type === TConfigItemType.SELECT) {
    return formatSelectDisplayValue(field, value);
  }
  if (field.type === TConfigItemType.SOURCE_OF_ACTIVE) {
    return formatSourceOfActiveDisplayLines(value).join(", ");
  }
  if (field.type === TConfigItemType.URL) {
    return formatUrlDisplayValue(value);
  }
  return value;
}
